import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SummaryWrapper } from '@app/wrappers/summary-wrapper/summary-wrapper';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import {ThemeModule} from '@app/themes/theme-module';
import {IMenuItem} from '@models/i-menu-item';
import {MenuChildrenPipe} from '@app/utils/pipes/menu-children.pipe';
import {SummaryParent} from '@app/wrappers/summary-parent';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { RegionService } from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import { UserService } from '@6grain/masofa-monolith-api-client/identity';
import { CropYieldForecastCalculationLogService } from '@6grain/masofa-monolith-api-client/ai-inference/api/cropYieldForecastCalculationLog.service';
import { CropYieldForecastCalculationLog, SortType } from '@6grain/masofa-monolith-api-client/ai-inference';
import { Region } from '@6grain/masofa-monolith-api-client/dictionaries';

interface SummaryData {
  model: string;
  module: string;
  version: string;
  description: string;
  lastRunDate: string;
  status: 'Active' | 'Processing' | 'Badge';
  result: string;
}

@Component({
  selector: 'app-crop-yield-forecasts-page',
  standalone: true,
  imports: [CommonModule, TranslateModule, ThemeModule, TableModule, ButtonModule, CardModule, MenuChildrenPipe],
  templateUrl: './crop-yield-forecasts-page.component.html',
  styleUrls: ['./crop-yield-forecasts-page.component.scss'],
})
export class CropYieldForecastsPageComponent extends SummaryParent  implements OnInit {

  recentLogs: CropYieldForecastCalculationLog[] = [];
  isLoading = false;
  currentLang: string = 'en';

  regionsMap: { [key: string]: Region } = {};
  usersMap: { [key: string]: string } = {};

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private calculationLogService: CropYieldForecastCalculationLogService,
    private regionService: RegionService,
    private userService: UserService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,

  ) {
    super()
  }

  ngOnInit(): void {
    this.currentLang = this.translate.currentLang || 'en';
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.currentLang = event.lang;
      this.cdr.markForCheck();
    });

    this.loadRecentAnalysis();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadRecentAnalysis(): void {
    this.isLoading = true;

    this.calculationLogService
      .aiInferenceCropYieldForecastCalculationLogGetByQueryPost({
        take: 5,
        offset: 0,
        sortBy: 'CreateAt',
        sort: SortType.NUMBER_1,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        catchError(() => of([])),
      )
      .subscribe((logs) => {
        this.recentLogs = logs || [];

        if (this.recentLogs.length > 0) {
          this.loadMissingRegions(this.recentLogs);
          this.loadUserNames(this.recentLogs);
        }
      });
  }

  loadMissingRegions(logs: CropYieldForecastCalculationLog[]): void {
    const missingIds = logs.map((l) => l.regionId).filter((id) => !!id && !this.regionsMap[String(id)]);

    const uniqueMissingIds = [...new Set(missingIds)];
    if (uniqueMissingIds.length === 0) return;

    const requests = uniqueMissingIds.map((id) =>
      this.regionService.dictionariesRegionGetByIdIdGet(String(id)).pipe(catchError(() => of(null))),
    );

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        results.forEach((region) => {
          if (region && region.id) {
            this.regionsMap[region.id] = region;
            this.regionsMap[region.id.toLowerCase()] = region;
          }
        });
        this.cdr.markForCheck();
      });
  }

  loadUserNames(logs: CropYieldForecastCalculationLog[]): void {
    const userIds = [
      ...new Set(logs.map((l) => l.createUser).filter((id) => !!id && id !== '00000000-0000-0000-0000-000000000000')),
    ];
    const idsToLoad = userIds.filter((id) => !this.usersMap[String(id)]);

    if (idsToLoad.length === 0) return;

    const requests = idsToLoad.map((id) =>
      this.userService.identityUserGetByIdIdGet(String(id)).pipe(catchError(() => of(null))),
    );

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe((users) => {
        users.forEach((user) => {
          if (user && user.id) {
            const displayName = user.userName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
            this.usersMap[user.id] = displayName || 'Unknown User';
          }
        });
        this.cdr.markForCheck();
      });
  }


  getRegionName(regionId: string | undefined): string {
    if (!regionId) return '-';
    const region = this.regionsMap[regionId] || this.regionsMap[regionId.toLowerCase()];
    if (!region) return regionId;

    const name =
      (region as any).names?.[this.currentLang] ||
      (region as any).names?.['en'] ||
      (region as any).name ||
      (region as any).Name ||
      'Unknown Region';

    return name;
  }

  getUserName(userId: string | undefined): string {
    if (!userId) return '-';
    return this.usersMap[userId] || userId;
  }

  getBadgeClass(status: string): string {
    if (!status) return 'badge';
    switch (status) {
      case 'Completed':
        return 'active';
      case 'Processing':
        return 'processing';
      case 'Failed':
      case 'CriticalFailure':
      case 'CompletedWithErrors':
        return 'failed';
      default:
        return 'badge';
    }
  }

  onExport(): void {
    console.log('Exporting data...');
  }
}
